import AppAssistantIcon from "@/assets/images/kk-assistant-app-logo.png";
import PlayerIcon from "@/assets/images/player_icon.png";
import { BrandSoftwareType } from "@/features/ui/types";
import classNames from "classnames";
import React from "react";
import Assistant from "./Assistant/Assistant";
import Download from "./Download/Download";
import Globe from "./Globe/Globe";
import styles from "./Icon.module.scss";
import KrossKast from "./KrossKast/KrossKast";
import MakeActive from "./MakeActive/MakeActive";
import Player from "./Player/Player";
import SendToProd from "./SendToProd/SendToProd";
import Trash from "./Trash/Trash";

// Define all possible icon names
export type IconName =
  | "assistant"
  | "download"
  | "globe"
  | "krosskast"
  | "makeActive"
  | "player"
  | "sendToProd"
  | "trash";

// A superset type that includes BrandSoftwareType values
export type IconNameType = IconName | BrandSoftwareType;

interface Props {
  name: IconNameType;
  size?: number;
  className?: string;
  color?: string;
  onClick?: () => void;
  useImage?: boolean;
}

const Icon: React.FC<Props> = ({
  name,
  size = 24,
  className,
  color = "currentColor",
  onClick,
  useImage = false,
  ...props
}) => {
  const getBrandIconSrc = (value: BrandSoftwareType) => {
    switch (value) {
      case "krosskast":
        return "https://staging-assets.krosskast.com/packs/media/images/check-good-1a4127f7a115ee5e73151187b4128643.png";
      case "assistant":
        return AppAssistantIcon;
      default:
        return PlayerIcon;
    }
  };

  if (useImage) {
    return (
      <img
        src={getBrandIconSrc(name as BrandSoftwareType)}
        alt={name as string}
        className={classNames(styles.imageIcon, className)}
        style={{ width: size, height: size }}
        onClick={onClick}
        {...(props as React.ImgHTMLAttributes<HTMLImageElement>)}
      />
    );
  }

  const getIconComponent = () => {
    switch (name) {
      case "assistant":
        return (
          <Assistant
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "download":
        return (
          <Download
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "globe":
        return (
          <Globe
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "krosskast":
        return (
          <KrossKast
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "makeActive":
        return (
          <MakeActive
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "player":
        return (
          <Player
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "sendToProd":
        return (
          <SendToProd
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      case "trash":
        return (
          <Trash
            width={size}
            height={size}
            className={classNames(styles.icon, className)}
            fill={color}
            onClick={onClick}
            {...props}
          />
        );
      default:
        return null;
    }
  };

  return getIconComponent();
};

export default Icon;
